import React, { useCallback, useEffect, useState } from 'react';

import ApprovalSettingsList from '../../admin/approvement/ApprovalSettingsList';
import ContingenciesApprovalsTab from '../../budget_change_management/ContingenciesApprovalsTab';

import SetupCard from './SetupCard';
import StepShell from './StepShell';

import { useToast } from 'Hooks/useToast';

/**
 * Approvals. Renders only the sections whose trigger is on — a section whose
 * trigger is off is absent, not disabled. Each section is a summary card
 * whose action expands its existing editor in place. Each editor saves on
 * its own; after a save the page data and the rail are refreshed, since
 * switching budget change thresholds off can take the section, or the whole
 * step, away.
 */
export default function ApprovalsStep({ step, urls, deferring, saving, onDefer, onNext, onRefresh }) {
    const [data, setData] = useState(null);
    const [expanded, setExpanded] = useState({});
    const toast = useToast();

    const fetchData = useCallback(
        () =>
            axios
                .get(urls.data)
                .then(({ data }) => setData(data))
                .catch(() => toast.error($t('Something went wrong. Please try again.'))),
        [urls.data],
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSaved = () => {
        fetchData();

        if (onRefresh) {
            onRefresh();
        }
    };

    const toggle = (section) => setExpanded((current) => ({ ...current, [section]: !current[section] }));

    const sections = data?.sections ?? [];
    const readOnly = step.read_only;

    const chainCard = (section, heading, lines) => (
        <SetupCard
            key={section}
            heading={heading}
            lines={lines}
            actionLabel={$t('+ Set up approval chain')}
            expanded={!!expanded[section]}
            onToggle={() => toggle(section)}
            regionId={`setup-wizard-approvals-${section}`}
        >
            <ApprovalSettingsList fetchUrl={urls.chains} updateUrl={urls.chains_update} triggerCodes={[section]} hideTitle onSaved={handleSaved} />
        </SetupCard>
    );

    return (
        <StepShell
            step={step}
            lede={$t('One place for every approval chain. This page shows only the approvals for what you have switched on, so nothing here is irrelevant.')}
            deferring={deferring}
            saving={saving}
            onDefer={onDefer}
            onNext={onNext}
        >
            {!data ? (
                <p className="tw-text-sm tw-text-charcoal-50">{$t('Loading…')}</p>
            ) : (
                <div className="tw-flex tw-flex-col tw-gap-y-2">
                    {sections.includes('pr') &&
                        chainCard('pr', $t('Purchase requisition approvals'), [
                            $t('Who approves PRs, at what thresholds, and in what order.'),
                            ...(data.approvals_module_on
                                ? []
                                : [$t('The Approvals module is off. This chain still applies to every purchase requisition raised on this project.')]),
                        ])}

                    {sections.includes('po') &&
                        chainCard('po', $t('Expenditure & direct commitment approvals'), [
                            $t('Who approves expenditure and direct commitments, and at what delegation levels.'),
                        ])}

                    {sections.includes('budget_change') && (
                        <SetupCard
                            heading={$t('Budget change approvals')}
                            lines={[
                                $t('Approval workflow for budget change requests, by department and value threshold.'),
                                $t('Contingency drawdown approvers — departmental and central.'),
                                $t('Who can baseline and re-baseline the budget.'),
                            ]}
                            actionLabel={$t('+ Set up approval workflow')}
                            expanded={!!expanded.budget_change}
                            onToggle={() => toggle('budget_change')}
                            regionId="setup-wizard-approvals-budget-change"
                        >
                            <ContingenciesApprovalsTab
                                rulesUrl={urls.budget_rules}
                                saveUrl={urls.budget_rules_update}
                                usersUrl={urls.users}
                                readOnly={readOnly || data.budget_read_only}
                                onSaved={handleSaved}
                            />
                        </SetupCard>
                    )}
                </div>
            )}
        </StepShell>
    );
}
